import { trpc } from "../../lib/trpc.js";
import { Botao, Entrada, Segmentos, Selecao, cx } from "../../components/ui.js";
import {
  ROTULO_DA_FOTO_DA_IA,
  ROTULO_DA_ORIGINAL,
  ROTULO_DA_PECA,
  legendaDaVersao,
} from "./ampliar-fotos.js";
import type { FotosPublicadas } from "./useFotosPublicadas.js";
import { SEM_VIVENCIA } from "./filtro-de-vivencia.js";
import { contagemDaPagina } from "./paginas-da-bandeja.js";

/**
 * Os filtros da bandeja de fotos: nome ou telefone, local e vivência — os mesmos da
 * Biblioteca —, e a contagem do que está na tela.
 *
 * A lista de vivências acompanha o local escolhido: sem isso o operador montaria
 * combinações que não existem e receberia uma tela vazia sem entender.
 *
 * No celular a busca ocupa a linha e o local e a vivência dividem a de baixo: três campos empilhados
 * comeriam a tela que é das fotos.
 */
export function FiltrosDasFotos({
  bandeja,
  contagem = true,
  className,
}: {
  bandeja: FotosPublicadas;
  /** Sem a linha "Mostrando N de M", para quem a põe em outro lugar (`ContagemDasFotos`). */
  contagem?: boolean;
  /** Troca o espaçamento de baixo (`mb-3`). */
  className?: string;
}) {
  const locais = trpc.institutions.list.useQuery({});
  const vivencias = trpc.vivencias.list.useQuery(
    bandeja.institutionId ? { institutionId: bandeja.institutionId } : undefined,
  );

  return (
    <div className={cx("flex flex-col gap-2", className ?? "mb-3")}>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <Entrada
          className="col-span-2 sm:col-span-1"
          type="search"
          placeholder="Nome ou telefone"
          aria-label="Buscar por nome ou telefone"
          value={bandeja.busca}
          onChange={(e) => bandeja.buscar(e.target.value)}
        />
        <Selecao
          // `truncate`: no celular os dois selects dividem a linha, e "Todas as vivências" não cabe.
          className="truncate"
          aria-label="Local"
          value={bandeja.institutionId}
          onChange={(e) => bandeja.escolherLocal(e.target.value)}
        >
          <option value="">Todos os locais</option>
          {locais.data?.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </Selecao>
        <Selecao
          className="truncate"
          aria-label="Vivência"
          value={bandeja.vivenciaId}
          onChange={(e) => bandeja.escolherVivencia(e.target.value)}
        >
          <option value="">Todas as vivências</option>
          {/* As do "Só fotografia" e as com IA sem vivência (#100); com um Local, só as dele. */}
          <option value={SEM_VIVENCIA}>Sem vivência</option>
          {vivencias.data?.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </Selecao>
      </div>

      {contagem && <ContagemDasFotos bandeja={bandeja} />}
    </div>
  );
}

/** "Mostrando N de M fotos" (ou a faixa da página, "9–16 de 37") e, havendo filtro, o "Limpar filtros". */
export function ContagemDasFotos({ bandeja }: { bandeja: FotosPublicadas }) {
  return (
    <div className="flex min-h-9 flex-wrap items-center justify-between gap-2 text-[0.8125rem] text-texto-suave">
      <p role="status">
        {bandeja.consulta.isFetching && bandeja.itens.length === 0
          ? "Buscando…"
          : bandeja.porPagina
            ? contagemDaPagina(
                bandeja.pagina,
                bandeja.porPagina,
                bandeja.itens.length,
                bandeja.total,
              )
            : `Mostrando ${bandeja.itens.length} de ${bandeja.total} ${bandeja.total === 1 ? "foto" : "fotos"}`}
      </p>
      {bandeja.filtrando && (
        <Botao tom="fantasma" formato="nu" className="min-h-11" onClick={bandeja.limparFiltros}>
          Limpar filtros
        </Botao>
      )}
    </div>
  );
}

/** "Mostrar mais", logo abaixo da grade; some quando já está tudo na tela. */
export function MostrarMaisFotos({ bandeja }: { bandeja: FotosPublicadas }) {
  if (!bandeja.temMais) return null;
  return (
    <div className="mt-3 flex justify-center">
      <Botao
        tom="quieto"
        className="min-h-11"
        carregando={bandeja.consulta.isFetching}
        onClick={bandeja.mostrarMais}
      >
        Mostrar mais
      </Botao>
    </div>
  );
}

/**
 * "Anterior · 2 de 5 · Próxima", logo abaixo da grade, quando a bandeja tem páginas (#103) e há mais de uma.
 *
 * `aoMudar` é avisado depois de cada troca: a tela devolve a grade à vista quando o topo dela ficou para trás.
 */
export function PaginacaoDasFotos({
  bandeja,
  aoMudar,
}: {
  bandeja: FotosPublicadas;
  aoMudar?: () => void;
}) {
  if (!bandeja.porPagina || bandeja.paginas <= 1) return null;
  const ir = (n: number) => {
    bandeja.irParaPagina(n);
    aoMudar?.();
  };
  return (
    <nav aria-label="Páginas das fotos" className="mt-3 flex items-center justify-between gap-2">
      <Botao
        tom="quieto"
        className="min-h-11"
        disabled={bandeja.pagina === 0}
        onClick={() => ir(bandeja.pagina - 1)}
      >
        Anterior
      </Botao>
      <span className="text-[0.8125rem] text-texto-suave" aria-live="polite">
        {bandeja.consulta.isFetching ? "Buscando…" : `${bandeja.pagina + 1} de ${bandeja.paginas}`}
      </span>
      <Botao
        tom="quieto"
        className="min-h-11"
        disabled={bandeja.pagina + 1 >= bandeja.paginas}
        onClick={() => ir(bandeja.pagina + 1)}
      >
        Próxima
      </Botao>
    </nav>
  );
}

/**
 * A chave das versões da foto, sobre a grade: "Peça com capa | Foto da IA", e "Foto original" onde a tela a
 * oferece (`comOriginal`, a Impressão). Só aparece quando alguma foto da lista tem outra versão além da peça;
 * as que não a têm seguem como estão em qualquer posição.
 */
export function ChaveDeVersaoDaFoto({
  bandeja,
  legendaPeca,
  legendaFoto,
  legendaOriginal,
  comOriginal = false,
  aparecer = bandeja.temVersaoDaIa || (comOriginal && bandeja.temVersaoOriginal),
  comLegenda = true,
}: {
  bandeja: FotosPublicadas;
  /** O que a versão "peça" quer dizer nesta tela. */
  legendaPeca: string;
  legendaFoto: string;
  legendaOriginal?: string;
  /** A tela oferece a foto original (a Impressão); os Marcadores, não. */
  comOriginal?: boolean;
  /** Quando mostrar a chave. Por padrão, quando alguma foto da lista tem outra versão além da peça. */
  aparecer?: boolean;
  /** Sem a legenda embaixo, para quem a põe em outro lugar. */
  comLegenda?: boolean;
}) {
  if (!aparecer) return null;
  return (
    <div className="mb-3">
      <Segmentos
        rotulo="Versão da foto"
        valor={bandeja.versao}
        opcoes={comOriginal ? OPCOES_COM_ORIGINAL : OPCOES_DE_VERSAO}
        aoMudar={bandeja.escolherVersao}
      />
      {comLegenda && (
        <p className="mt-1.5 text-[0.75rem] leading-snug text-texto-suave">
          {legendaDaVersao(bandeja.versao, legendaPeca, legendaFoto, legendaOriginal)}
        </p>
      )}
    </div>
  );
}

const OPCOES_DE_VERSAO = [
  { id: "peca", rotulo: ROTULO_DA_PECA },
  { id: "foto", rotulo: ROTULO_DA_FOTO_DA_IA },
] as const;

const OPCOES_COM_ORIGINAL = [
  ...OPCOES_DE_VERSAO,
  { id: "original", rotulo: ROTULO_DA_ORIGINAL },
] as const;
